'use client'

import React, { useEffect, useRef, useState } from 'react'
import { ChevronDown, ChevronRight, X } from 'lucide-react'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses } from '../ProcessedEntry/DomainEntry/processedTypes'
import { WidgetModal } from '../Widget'
import { useIsMobile } from '@/hooks/use-mobile'

type Accent = 'sky' | 'red' | 'amber' | 'emerald' | 'none'

const ACCENT_BORDER: Record<Accent, string> = {
  sky:     'border-signal/30',
  red:     'border-destructive/40',
  amber:   'border-warning-border',
  emerald: 'border-success/40',
  none:    '',
}

const ACCENT_LABEL: Record<Accent, string> = {
  sky:     'text-signal',
  red:     'text-destructive',
  amber:   'text-warning',
  emerald: 'text-success',
  none:    '',
}

// Layout steps are keyed to the width of the detail shell's CONTENT area
// (EntryDetailShell marks it `@container/detail`), not to the viewport: an
// investigation sidebar or the app's own nav can eat a third of a wide
// screen, and a viewport breakpoint kept the 4-up layout while every panel
// was squeezed to ~200px (values clipped, the message mockup crushed). Three
// steps, shared by the shell's grid, every section grid on a detail page
// (detailGridCols) and the panels' spans (COL_SPAN/ROW_SPAN) so a span can
// never exceed its grid:
//   < 48rem  (@3xl): one stacked column, every panel full width
//   < 72rem  (@6xl): two columns — a cols=1 panel takes half, cols>=2 full
//   >= 72rem       : the full cols-wide layout
// Literal strings on purpose — Tailwind only generates classes it can read
// whole in the source, so these can't be built by interpolation.
const DETAIL_GRID_COLS: Record<1 | 2 | 3 | 4 | 5, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 @3xl/detail:grid-cols-2',
  3: 'grid-cols-1 @3xl/detail:grid-cols-2 @6xl/detail:grid-cols-3',
  4: 'grid-cols-1 @3xl/detail:grid-cols-2 @6xl/detail:grid-cols-4',
  5: 'grid-cols-1 @3xl/detail:grid-cols-2 @6xl/detail:grid-cols-5',
}

export function detailGridCols(cols: 1 | 2 | 3 | 4 | 5): string {
  return DETAIL_GRID_COLS[cols]
}

// For a panel that is not an EntryDetailPanel (a bare div or a Widget placed
// straight into the shell's grid): the span that matches the grid's own
// column steps. A viewport `sm:col-span-3` on a grid that is two columns
// wide at this container width creates an implicit third column and
// squeezes the two real ones -- the candidate detail did exactly that.
export function detailColSpan(cols: 1 | 2 | 3 | 4 | 5): string {
  return COL_SPAN[cols]
}

const COL_SPAN: Record<1 | 2 | 3 | 4 | 5, string> = {
  1: 'col-span-1',
  2: 'col-span-1 @3xl/detail:col-span-2',
  3: 'col-span-1 @3xl/detail:col-span-2 @6xl/detail:col-span-3',
  4: 'col-span-1 @3xl/detail:col-span-2 @6xl/detail:col-span-4',
  5: 'col-span-1 @3xl/detail:col-span-2 @6xl/detail:col-span-5',
}

// Row spans only exist in the full layout: below it panels size to their own
// content (the shell also only applies its gridTemplateRows there).
const ROW_SPAN: Record<1 | 2 | 3, string> = {
  1: 'row-span-1',
  2: 'row-span-1 @6xl/detail:row-span-2',
  3: 'row-span-1 @6xl/detail:row-span-3',
}

// Matches ROW_SPAN's own 280px-per-row assumption (EntryDetailShell's
// gridTemplateRows="280px 280px 280px", gap-3 = 12px between tracks) — but
// applied as a real max-height on the content itself, not just inferred
// from the ambient grid track. EntryDetailShell only sets that
// gridTemplateRows style `!isMobile` (mobile panels are meant to size to
// their own collapsed-by-default content instead), so a scrollable panel
// with no cap of its own had nothing bounding it there: a tall pipeline
// step's JSON dump grew the whole page instead of scrolling inside its own
// panel, and even collapsing that step back afterward left the page's
// scroll position stranded wherever it had scrolled to — reading as "the
// height doesn't go back down" even though the DOM itself had shrunk.
const MAX_HEIGHT: Record<1 | 2 | 3, string> = {
  1: 'max-h-[280px]',
  2: 'max-h-[572px]',
  3: 'max-h-[864px]',
}

export function EntryDetailPanel({
  label,
  children,
  theme,
  cols = 1,
  rows = 1,
  accent = 'none',
  className = '',
  noPadding = false,
  scrollable = false,
  onRemove,
  headerRight,
}: {
  label: string
  children: React.ReactNode
  theme: ProcessedTheme
  cols?: 1 | 2 | 3 | 4 | 5
  rows?: 1 | 2 | 3
  accent?: Accent
  className?: string
  noPadding?: boolean
  scrollable?: boolean
  onRemove?: () => void
  /** Extra control (e.g. a refresh button) rendered before the built-in
   * expand/remove buttons in the header's action cluster. */
  headerRight?: React.ReactNode
}) {
  const c = watchClasses(theme)
  const borderCls = accent !== 'none' ? ACCENT_BORDER[accent] : c.line
  const labelCls  = accent !== 'none' ? ACCENT_LABEL[accent]  : c.label
  const [expanded, setExpanded] = useState(false)

  // Widgets default to open on desktop, but a page full of them stacked
  // single-column on mobile (see COL_SPAN/ROW_SPAN above) is a lot of
  // scrolling — so on mobile they start collapsed to just their header,
  // tap to open. Applied once, the first time we learn we're on mobile,
  // via a ref rather than keying off isMobile directly — a later resize
  // (e.g. rotating a tablet) shouldn't stomp on a collapse/expand the user
  // already chose.
  const isMobile = useIsMobile()
  const [collapsed, setCollapsed] = useState(false)
  const appliedMobileDefault = useRef(false)
  useEffect(() => {
    if (isMobile && !appliedMobileDefault.current) {
      appliedMobileDefault.current = true
      setCollapsed(true)
    }
  }, [isMobile])

  return (
    <>
      {expanded && (
        <WidgetModal label={label} theme={theme} onClose={() => setExpanded(false)}>
          {children}
        </WidgetModal>
      )}
      <section
        className={`${COL_SPAN[cols]} ${ROW_SPAN[rows]} flex flex-col border ${borderCls} ${c.panel} overflow-hidden ${className}`}
      >
        <div
          onClick={() => setCollapsed((v) => !v)}
          className={`shrink-0 flex cursor-pointer items-center gap-3 border-b px-4 py-2.5 ${borderCls}`}
        >
          {collapsed ? <ChevronRight className={`h-3 w-3 shrink-0 ${c.faint}`} /> : <ChevronDown className={`h-3 w-3 shrink-0 ${c.faint}`} />}
          <span className={`font-mono text-[10px] uppercase tracking-[0.28em] ${labelCls}`}>
            // {label}
          </span>
          <div className="ml-auto flex shrink-0 items-center gap-1" onClick={(e) => e.stopPropagation()}>
            {headerRight}
            <button
              onClick={() => setExpanded(true)}
              className={`rounded p-1 transition-colors hover:text-signal ${c.faint}`}
              aria-label="Expand"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" />
                <line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" />
              </svg>
            </button>
            {onRemove && (
              <button
                onClick={onRemove}
                className={`rounded p-1 transition-colors hover:text-destructive ${c.faint}`}
                aria-label="Remove widget"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>
        {!collapsed && (
          <div className={`flex-1 ${scrollable ? `overflow-y-auto ${MAX_HEIGHT[rows]}` : 'overflow-hidden'} ${noPadding ? '' : 'p-4'}`}>
            {children}
          </div>
        )}
      </section>
    </>
  )
}
